// A trainer's look: skin tone, hair style and hair colour, chosen when the trainer is made and changed for free
// any time after. Outfits (shared/outfits.ts) go over it. Looks only: they never change stats, odds or rewards.
//
// The trainer art (art/rig3d/build-trainer.ts) is drawn once per outfit and hair style, in the default skin tone
// and hair colour (the "key" ramps below); every other skin tone and hair colour is the same picture with the key
// ramps' colours swapped for its own (server/lookart.ts). So the key ramps can't change without a rebuild, and no other
// part of a trainer may use their colours (build-trainer.ts checks).
//
// A look travels as a short code, `s<skin>h<style>c<colour>` (one digit each, "s1h0c1" is the default), on the
// trainer (TrainerDTO.look) and, after a "~", on the outfit id the server sends with other trainers ("keeper~s3h2c4"),
// so every portrait of a trainer shows their look.

export interface Look {
  /** Index into SKIN_TONES. */
  skin: number;
  /** Index into HAIR_STYLES. */
  hair: number;
  /** Index into HAIR_COLORS. */
  color: number;
}

/** A five-step colour ramp, darkest first (art/rig3d palette convention). */
export type LookRamp = readonly [string, string, string, string, string];

export interface SkinTone { name: string; ramp: LookRamp; /** Blush on the cheeks. */ cheek: string }
export interface HairColor { name: string; ramp: LookRamp }
export interface HairStyle {
  id: string;
  name: string;
  /** Shown under a hood, hat or helmet too (long hair falls below it); the others are hidden under one. */
  covered?: boolean;
}

export const SKIN_TONES: readonly SkinTone[] = [
  { name: 'Porcelain', ramp: ['#6e4038', '#c88e7c', '#f2c4ac', '#fde2d0', '#fff6ee'], cheek: '#f4a8ac' },
  // The key ramp: the trainer art is drawn in it (a rebuild is needed to change it).
  { name: 'Peach', ramp: ['#6a3a2e', '#c27e62', '#eeb48e', '#fcd6b4', '#fff0e0'], cheek: '#f4a2a8' },
  { name: 'Tan', ramp: ['#58301e', '#a86a48', '#d29a70', '#eabc92', '#f8dcbc'], cheek: '#e88c84' },
  { name: 'Bronze', ramp: ['#46220f', '#8a5232', '#b87a50', '#d69c6c', '#eebe92'], cheek: '#d4786a' },
  { name: 'Umber', ramp: ['#2e160c', '#62361e', '#8c5434', '#ae7248', '#c89266'], cheek: '#b45c4c' },
  { name: 'Ebony', ramp: ['#1a0c08', '#3a1e12', '#5a3422', '#7a4a32', '#9a6a4c'], cheek: '#8e4436' },
];

export const HAIR_STYLES: readonly HairStyle[] = [
  { id: 'tousled', name: 'Tousled' },
  { id: 'short', name: 'Short' },
  { id: 'long', name: 'Long', covered: true },
  { id: 'ponytail', name: 'Ponytail', covered: true },
  { id: 'bun', name: 'Bun' },
];

export const HAIR_COLORS: readonly HairColor[] = [
  { name: 'Black', ramp: ['#0a090e', '#18161e', '#282530', '#3c3846', '#58546a'] },
  // The key ramp.
  { name: 'Chestnut', ramp: ['#1a0f0c', '#3a2218', '#5a3622', '#80502e', '#a87242'] },
  { name: 'Auburn', ramp: ['#220a08', '#4c1a12', '#7a2e1c', '#a4482a', '#c8703e'] },
  { name: 'Copper', ramp: ['#3a1206', '#74280c', '#ac4a18', '#d8742c', '#f4a050'] },
  { name: 'Blonde', ramp: ['#4a3414', '#86642a', '#c09a48', '#e2c46c', '#f8e4a0'] },
  { name: 'Silver', ramp: ['#34364a', '#6a6e84', '#a2a8bc', '#d2d8e6', '#f6f8fc'] },
  { name: 'Midnight', ramp: ['#060a1a', '#101c3a', '#1c3062', '#2c4a8c', '#4a70b6'] },
  { name: 'Rose', ramp: ['#2e0a1e', '#5a1638', '#8a2a58', '#b84a7e', '#e07aa6'] },
];

export const DEFAULT_LOOK: Readonly<Look> = { skin: 1, hair: 0, color: 1 };
/** The ramps the art is drawn in. */
export const KEY_SKIN = SKIN_TONES[DEFAULT_LOOK.skin];
export const KEY_HAIR = HAIR_COLORS[DEFAULT_LOOK.color];

const inRange = (n: unknown, len: number): n is number => Number.isInteger(n) && (n as number) >= 0 && (n as number) < len;

export function validLook(l: unknown): l is Look {
  const o = l as Look | null;
  return !!o && inRange(o.skin, SKIN_TONES.length) && inRange(o.hair, HAIR_STYLES.length) && inRange(o.color, HAIR_COLORS.length);
}

export const lookCode = (l: Look): string => `s${l.skin}h${l.hair}c${l.color}`;

/** A look from its code; null for anything malformed or out of range. */
export function parseLook(code: string | null | undefined): Look | null {
  const m = /^s(\d)h(\d)c(\d)$/.exec(code ?? '');
  if (!m) return null;
  const l = { skin: +m[1], hair: +m[2], color: +m[3] };
  return validLook(l) ? l : null;
}

/** The look a trainer has: their own, or the default. */
export const lookOf = (code: string | null | undefined): Look => parseLook(code) ?? { ...DEFAULT_LOOK };

export const isDefaultLook = (l: Look) => l.skin === DEFAULT_LOOK.skin && l.hair === DEFAULT_LOOK.hair && l.color === DEFAULT_LOOK.color;

/** What a trainer wears, as sent with other trainers: the outfit id, then "~" and the look unless it is the default. */
export function wornCode(outfit: string, look: string | null | undefined): string {
  const l = parseLook(look);
  return l && !isDefaultLook(l) ? `${outfit}~${lookCode(l)}` : outfit;
}

/** The outfit id and look in a worn code (or a bare outfit id). */
export function splitWorn(worn: string | null | undefined): { outfit: string; look: Look } {
  const [outfit, look] = (worn ?? '').split('~');
  return { outfit, look: lookOf(look) };
}
